import React, { useEffect, useState, lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { supabase } from "./lib/supabaseClient";
import { AuthProvider } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/toaster";

// ✅ Lazy-load PAGES (big wins)
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

const Auth = lazy(() => import("./pages/Auth"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

const CompanyDashboard = lazy(() => import("./pages/CompanyDashboard"));
const HostDashboard = lazy(() => import("./pages/HostDashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));

// ✅ Lazy-load HOME SECTIONS too (optional but recommended)
const Hero = lazy(() => import("./components/Hero"));
const About = lazy(() => import("./components/About"));
const HowItWorks = lazy(() => import("./components/HowItWorks"));
const Forms = lazy(() => import("./components/Forms"));
const FAQ = lazy(() => import("./components/FAQ"));
const Footer = lazy(() => import("./components/Footer"));

// Protected Route Component (company/host)
const ProtectedRoute = ({ children, requiredType }) => {
  const [checking, setChecking] = useState(true);
  const [sessionUser, setSessionUser] = useState(null);

  useEffect(() => {
    const run = async () => {
      const { data, error } = await supabase.auth.getUser();
      if (error) console.error(error);
      setSessionUser(data?.user || null);
      setChecking(false);
    };
    run();
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!sessionUser) return <Navigate to="/auth" replace />;

  const userType =
    sessionUser.user_metadata?.userType ||
    sessionUser.user_metadata?.user_type ||
    "company";

  if (requiredType && userType !== requiredType) {
    return (
      <Navigate
        to={userType === "host" ? "/dashboard/host" : "/dashboard/company"}
        replace
      />
    );
  }

  return children;
};

// Admin Protected Route (checks admin_users table)
const AdminProtectedRoute = ({ children }) => {
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    const run = async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u?.user) {
        setAllowed(false);
        setChecking(false);
        return;
      }

      // If user is NOT admin, RLS will block this select
      const { data, error } = await supabase
        .from("admin_users")
        .select("user_id")
        .limit(1);

      // Note: (data || []).length >= 0 will always be true if no error.
      setAllowed(!error);
      setChecking(false);
    };
    run();
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!allowed) return <Navigate to="/" replace />;
  return children;
};

const Home = () => {
  return (
    <div className="min-h-screen dark bg-[#05070b] text-white">
      <Hero />
      <About />
      <HowItWorks />
      <Forms />
      <FAQ />
      <Footer />
    </div>
  );
};

function AppContent() {
  return (
    <div className="App">
      {/* ✅ Suspense wraps all lazy routes/components */}
      <Suspense
        fallback={
          <div className="min-h-screen flex items-center justify-center bg-[#05070b] text-white">
            <p>Loading...</p>
          </div>
        }
      >
        <Routes>
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />

          <Route path="/" element={<Home />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route
            path="/dashboard/company"
            element={
              <ProtectedRoute requiredType="company">
                <CompanyDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/host"
            element={
              <ProtectedRoute requiredType="host">
                <HostDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <AdminProtectedRoute>
                <AdminDashboard />
              </AdminProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        <Toaster />
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
